import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Plus,
  Save,
  Search,
  ShieldCheck,
  Lock,
  UserCog,
  Stethoscope,
  Building2,
  BriefcaseBusiness,
  Smartphone,
} from "lucide-react";

const MODULE_GROUPS = [
  {
    title: "Clinical",
    icon: Stethoscope,
    items: [
      ["queue", "Reception Queue"],
      ["calendar_reservations", "Reservations"],
      ["patients", "Patients"],
      ["clinical_notes", "Clinical Notes / SOAP"],
      ["prescriptions", "Prescriptions"],
      ["medical_certificates", "Medical Certificates"],
      ["laboratory", "Laboratory"],
      ["imaging", "Imaging"],
      ["patient_qr_card", "Patient QR Cards"],
      ["followups", "Follow-ups"],
    ],
  },
  {
    title: "Business",
    icon: BriefcaseBusiness,
    items: [
      ["medicines", "Medicines"],
      ["inventory", "Inventory"],
      ["pharmacy_pos", "POS / Billing"],
      ["pos_services", "POS Services"],
      ["pos_reports", "POS Reports"],
      ["doctor_business", "Doctor Business"],
      ["referrals", "Referrals"],
      ["referral_partners", "Referral Partners"],
    ],
  },
  {
    title: "Administration",
    icon: UserCog,
    items: [
      ["users", "User Accounts"],
      ["doctors", "Doctors"],
      ["doctor_groups", "Doctor Groups"],
      ["patient_access_control", "Patient Access Control"],
      ["branches", "Branches"],
      ["settings", "Print Settings"],
      ["reports", "Reports"],
      ["manual", "User Manual"],
    ],
  },
  {
    title: "Mobile / PWA",
    icon: Smartphone,
    items: [
      ["doctor_mobile", "Doctor Mobile PWA"],
      ["mobile_patients", "Mobile Patients"],
      ["mobile_prescriptions", "Mobile Prescriptions"],
      ["mobile_certificates", "Mobile Certificates"],
      ["mobile_billing", "Mobile Billing"],
    ],
  },
];

const ROLES = [
  "hospital_admin",
  "doctor",
  "reception",
  "staff",
  "cashier",
  "pharmacist",
  "lab",
  "billing",
];

const blankForm = {
  hospital_id: "",
  name: "",
  email: "",
  password: "",
  role: "staff",
  status: "active",
  module_overrides: {},
};

export default function PlatformUsers() {
  const [hospitals, setHospitals] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState("");
  const [hospitalModules, setHospitalModules] = useState({});
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);

  const loadHospitals = async () => {
    try {
      const { data } = await api.get("/platform/hospitals");
      const rows = Array.isArray(data) ? data : [];
      setHospitals(rows);
      if (!selectedHospital && rows[0]?.id) setSelectedHospital(rows[0].id);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const loadHospitalModules = async (hid) => {
    if (!hid) return;
    try {
      const { data } = await api.get(`/platform/hospitals/${hid}/modules`);
      setHospitalModules(data?.modules || {});
    } catch {
      setHospitalModules({});
    }
  };

  const loadUsers = async (hid = selectedHospital) => {
    if (!hid) return;
    try {
      const { data } = await api.get("/platform/users", { params: { hospital_id: hid } });
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  useEffect(() => {
    loadHospitals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedHospital) return;
    loadHospitalModules(selectedHospital);
    loadUsers(selectedHospital);
    setForm((f) => ({ ...f, hospital_id: selectedHospital }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHospital]);

  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) =>
      [u.name, u.email, u.role, u.hospital_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [users, q]);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...blankForm, hospital_id: selectedHospital });
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      hospital_id: u.hospital_id,
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "staff",
      status: u.status || "active",
      module_overrides: u.module_overrides || {},
    });
  };

  const hospitalAllowsModule = (key) => hospitalModules[key] !== false;

  const userShowsModule = (key) => {
    if (!hospitalAllowsModule(key)) return false;
    return (form.module_overrides || {})[key] !== false;
  };

  const toggleModule = (key) => {
    if (!hospitalAllowsModule(key)) return;

    const next = { ...(form.module_overrides || {}) };
    next[key] = next[key] === false ? true : false;
    setForm({ ...form, module_overrides: next });
  };

  // Platform admin resets an existing user's password. The password lives on the
  // user document (not per-hospital), so resetting via any hospital the user
  // belongs to changes it everywhere they sign in.
  const resetPassword = async () => {
    if (!editing?.user_id) return;
    if (!form.password || form.password.length < 4) {
      toast.error("Enter a new password (at least 4 characters)");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/platform/users/${editing.user_id}/hospital/${editing.hospital_id}`, {
        password: form.password,
      });
      toast.success(`Password reset for ${editing.name || editing.email}`);
      setForm((f) => ({ ...f, password: "" }));
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form.hospital_id || !form.name.trim() || !form.email.trim()) {
      toast.error("Hospital, name, and email are required");
      return;
    }

    if (!editing && !form.password) {
      toast.error("Password is required for new users");
      return;
    }

    setBusy(true);
    try {
      if (editing?.user_id) {
        await api.put(`/platform/users/${editing.user_id}/hospital/${editing.hospital_id}`, {
          name: form.name,
          role: form.role,
          roles: [form.role],
          status: form.status,
          module_overrides: form.module_overrides || {},
        });
        toast.success("User access updated");
      } else {
        await api.post("/platform/users", {
          hospital_id: form.hospital_id,
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
          roles: [form.role],
          status: form.status,
          module_overrides: form.module_overrides || {},
        });
        toast.success("User created");
      }

      await loadUsers(form.hospital_id);
      startCreate();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto" data-testid="platform-users-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Platform Users
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Create hospital users remotely, change roles, and control each user's sidebar modules.
          </p>
        </div>

        <Button onClick={startCreate} className="bg-blue-700 hover:bg-blue-800">
          <Plus size={16} className="mr-2" />
          New User
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[430px_1fr] gap-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users size={17} />
              Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-3">
              <select
                value={selectedHospital}
                onChange={(e) => setSelectedHospital(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              >
                {hospitals.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.hospital_name || h.name || h.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users..." className="pl-9" />
            </div>

            <div className="space-y-2 max-h-[70vh] overflow-auto pr-1">
              {filteredUsers.map((u) => (
                <button
                  key={`${u.user_id}-${u.hospital_id}`}
                  onClick={() => startEdit(u)}
                  className={`w-full text-left rounded-xl border p-4 transition ${
                    editing?.user_id === u.user_id ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 truncate">{u.name || u.email}</div>
                      <div className="text-xs text-slate-500 mt-1 truncate">{u.email}</div>
                      <div className="text-xs text-slate-400 mt-2">{u.role}</div>
                    </div>
                    <Badge className={u.status === "active" ? "bg-emerald-600" : "bg-slate-500"}>{u.status}</Badge>
                  </div>
                </button>
              ))}

              {filteredUsers.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-8">No users found.</div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck size={17} />
                {editing ? "Edit User Access" : "Create Hospital User"}
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Hospital</div>
                  <select
                    value={form.hospital_id}
                    onChange={(e) => setForm({ ...form, hospital_id: e.target.value })}
                    disabled={!!editing}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white disabled:bg-slate-100"
                  >
                    <option value="">Select hospital</option>
                    {hospitals.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name || h.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Role</div>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Name</div>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Email</div>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} />
                </div>
              </div>

              {!editing ? (
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Temporary Password</div>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
              ) : (
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Reset Password</div>
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="New password"
                      autoComplete="new-password"
                      data-testid="reset-password-input"
                    />
                    <Button
                      onClick={resetPassword}
                      disabled={busy || !form.password}
                      className="bg-amber-600 hover:bg-amber-700 shrink-0"
                      data-testid="btn-reset-password"
                    >
                      <Lock size={14} className="mr-1" />
                      Reset
                    </Button>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Sets a new password for {editing.email}. Give it to the user — they can change it later under My Profile.
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Status</div>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 size={17} />
                Sidebar Modules
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-5">
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                User modules can only reduce visibility. A locked module is disabled at hospital/platform level and cannot be shown to this user.
              </div>

              {MODULE_GROUPS.map((group) => {
                const Icon = group.icon;
                return (
                  <div key={group.title}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={16} className="text-slate-500" />
                      <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{group.title}</div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                      {group.items.map(([key, label]) => {
                        const hospitalEnabled = hospitalAllowsModule(key);
                        const visible = userShowsModule(key);

                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => toggleModule(key)}
                            disabled={!hospitalEnabled}
                            className={`rounded-xl border p-3 text-left transition ${
                              !hospitalEnabled
                                ? "border-slate-200 bg-slate-100 opacity-60 cursor-not-allowed"
                                : visible
                                  ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                                  : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-semibold text-sm text-slate-900">{label}</div>

                              {!hospitalEnabled ? (
                                <Badge variant="outline" className="gap-1">
                                  <Lock size={11} />
                                  Locked
                                </Badge>
                              ) : (
                                <Badge className={visible ? "bg-emerald-600" : "bg-slate-500"}>
                                  {visible ? "Shown" : "Hidden"}
                                </Badge>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <Button onClick={save} disabled={busy} className="w-full h-12 bg-blue-700 hover:bg-blue-800">
                <Save size={16} className="mr-2" />
                {editing ? "Save User Access" : "Create User"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
